import { useMutation } from "@tanstack/react-query";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import { downloadGeneratedContract } from "../../api/contracts";

function extractFileNameFromURL(url: string): string {
  const pathname = new URL(url).pathname;
  const filename = pathname.substring(pathname.lastIndexOf("/") + 1);
  return decodeURIComponent(filename.split(";")[0]);
}

function useDownloadGeneratedContracts() {
  const mutation = useMutation({
    mutationFn: async ({ contractUids }: { contractUids: string[] }) => {
      const results = {
        successful: 0,
        failed: 0,
        errors: [] as Array<{ uid: string; error: string }>,
      };

      for (const contractUid of contractUids) {
        try {
          const response = await downloadGeneratedContract({ contractUid });

          const responses = await fetch(response.data.url);
          const blob = await responses.blob();

          const downloadUrl = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = downloadUrl;
          a.download = extractFileNameFromURL(response.data.url);
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(downloadUrl);

          results.successful++;
        } catch (error) {
          results.failed++;
          results.errors.push({
            uid: contractUid,
            error: error instanceof Error ? error.message : "Unknown error",
          });
        }
      }

      return results;
    },
    onSuccess: (results) => {
      if (results.failed > 0) {
        showToast({
          type: ToastType.WARNING,
          message: `Downloaded ${results.successful} file(s), ${results.failed} failed`,
          toastId: TOAST_IDS.DOWNLOAD_DOCUMENT_ERROR,
          options: {
            position: "bottom-left",
            autoClose: 3000,
          },
        });
      } else if (results.successful > 0) {
        showToast({
          type: ToastType.SUCCESS,
          message: `Successfully downloaded ${results.successful} file(s)`,
          toastId: TOAST_IDS.DOWNLOAD_DOCUMENT_SUCCESS,
          options: {
            position: "bottom-left",
            autoClose: 3000,
          },
        });
      }
    },
    onError: () => {
      showToast({
        type: ToastType.ERROR,
        message: "Failed to download contracts",
        toastId: TOAST_IDS.DOWNLOAD_DOCUMENT_ERROR,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
    },
  });

  return {
    download: mutation.mutate,
    isDownloading: mutation.isPending,
    isError: mutation.isError,
    results: mutation.data,
  };
}

export default useDownloadGeneratedContracts;
